// src/utils/environment.js
// 환경(sandbox/production) 및 플랫폼(pi/web) 구분을 위한 공통 유틸리티 함수
// 🔧 [Pi Browser 실제 환경 대응 + 성능 최적화 + Remote Config 연동] 완료 버전
// ✅ [2025-08-06 수정] URL 파라미터로 testnet 환경 강제 지정 기능 추가
// ✅ [2025-08-06 추가 수정] Remote Config 초기화 전에도 로그 출력 가능하도록 개선
// ✅ [2025-08-10 추가] 메인넷 App ID 감지 로직 추가
// ✅ [2025-08-12 추가] PC 샌드박스 환경 감지 함수 추가
// 🎯 [2025-08-18 핵심 수정] Pi SDK environment를 최우선으로 사용하도록 전면 개선
// 🔥 [2025-08-19 최종 수정] Pi Browser 추측 로직 완전 제거 - 명확한 근거만 사용

// Remote Config 연동을 위한 import 추가 (현재 사용하지 않음 - 향후 확장용)
// import { getConfigString } from '../services/remoteConfig';

/**
 * [최적화] 캐시 객체 - 환경 체크 결과를 저장하여 중복 계산 방지
 */
const ENVIRONMENT_CACHE = {
  platform: null,
  environment: null,
  firebaseEnvironment: null,
  displayEnvironment: null,
  isDevelopment: null,
  shouldLoadPiSDK: null,
  environmentInfo: null
};

/**
 * [성능 최적화] 로그 제어 캐시 - URL 파라미터 매번 파싱 방지 (현재 사용하지 않음)
 */
// let LOG_ACCESS_CACHE = null;
// let LOG_ACCESS_CHECKED = false;

/**
 * 로그 액세스 권한 확인 함수
 * - MY > 설정 > 버전 5회 탭으로 개발자 모드 켠 뒤, "디버그 모드" 켜면 로그/에러 패널 표시
 * - 또는 URL ?debug=<REACT_APP_LOG_ACCESS_KEY> 로 접속 시 동일
 */
const hasLogAccess = () => {
  try {
    // 1) 설정 기반: 개발자 모드 + 디버그 모드 둘 다 켜진 경우 (MY > 설정 > 버전 5회 탭 > 디버그 모드 ON)
    if (typeof localStorage !== 'undefined') {
      if (localStorage.getItem('dev_mode') === 'true' && localStorage.getItem('debug_mode') === 'true') {
        return true;
      }
    }

    // 2) URL 파라미터 확인 (기존 방식)
    const urlParams = new URLSearchParams(window.location.search);
    const debugParam = urlParams.get('debug');
    if (!debugParam) return false;

    const accessKey = process.env.REACT_APP_LOG_ACCESS_KEY;
    return debugParam === accessKey && !!accessKey;

  } catch (error) {
    console.error('[Environment] 로그 액세스 검증 중 오류:', error);
    return false;
  }
};

/**
 * 조건부 로그 출력 - 성능 최적화 및 보안 강화
 */
export function debugLog(...args) {
  if (hasLogAccess()) {
    console.log(...args);
  }
}

// 🚀 [신규] 콘솔 로그 필터링 시스템
let originalConsoleLog = console.log;
let originalConsoleWarn = console.warn;
let isLogFilteringEnabled = false;

/**
 * Pi SDK / 브라우저 확장이 던지는 콘솔 노이즈 억제
 * - Pi 번들은 첫 인자로 문자열이 아닌 MessageEvent 객체를 넘기는 경우가 많음 (기존 문자열 필터 회피)
 * - React DevTools / MetaMask 등 postMessage 기반 확장 트래픽
 */
const shouldSuppressNoisyConsoleArgs = (args) => {
  if (!args || args.length === 0) return false;

  for (let i = 0; i < args.length; i++) {
    const a = args[i];
    if (a instanceof MessageEvent) return true;
    if (a && typeof a === 'object') {
      const ctor = a.constructor && a.constructor.name;
      if (ctor === 'MessageEvent') return true;

      if (a.target === 'metamask-inpage') return true;
      if (a.data && typeof a.data === 'object' && a.data.target === 'metamask-inpage') return true;

      const src = a.source;
      if (src === 'react-devtools-content-script' ||
        src === 'react-devtools-bridge' ||
        src === 'react-devtools-backend-manager') {
        return true;
      }
      if (a.frontend_url && a.development_url && a.slug) return true;
    }
    if (typeof a === 'string') {
      if (/Discarding message|current app not loaded|data is logged below|postMessageToIFrame|Iframe loaded, ready to exchange/i.test(a)) {
        return true;
      }
      if (/Prod:\s*(true|false)\s*\|\s*Sandbox:\s*(true|false)/i.test(a)) return true;
    }
  }
  return false;
};

/**
 * Pi SDK 메시지 필터링 활성화
 * 사용자 편의성을 위한 로그 정리
 */
export const enableLogFiltering = () => {
  if (isLogFilteringEnabled) return;

  isLogFilteringEnabled = true;

  console.log = (...args) => {
    if (shouldSuppressNoisyConsoleArgs(args)) return;

    const message = args[0];

    // 🔧 [보안 강화] 민감한 정보 필터링 추가
    if (typeof message === 'string') {
      // Pi SDK 관련 메시지 필터링
      if (message.includes('Discarding message') ||
        message.includes('{source: \'react-devtools-content-script\', hello: true}') ||
        message.includes('{source: \'react-devtools-bridge\', payload: {…}}') ||
        message.includes('{source: \'react-devtools-backend-manager\', payload: {…}}') ||
        message.includes('{source: \'react-devtools-content-script\', payload: {…}}') ||
        message.includes('current app not loaded') ||
        message.includes('data is logged below') ||
        message.includes('Prod: false | Sandbox: false') ||
        message.includes('Prod: true | Sandbox: true') ||
        message.includes('MessageEvent') ||
        message.includes('postMessageToIFrame') ||
        message.includes('Iframe loaded, ready to exchange messages') ||
        message.includes('SDKMessaging instantiated') ||
        message.includes('Iframe loaded, ready to exchange messages') ||
        message.includes('postMessageToIFrame {id:') ||
        message.includes('MessageEvent {isTrusted: true, data:') ||
        message.includes('{frontend_url:') ||
        message.includes('{development_url:') ||
        message.includes('{slug:') ||
        message.includes('{name:') ||
        message.includes('{conversationId:') ||
        message.includes('{source: \'react-devtools-bridge\'') ||
        message.includes('{source: \'react-devtools-content-script\'') ||
        message.includes('{source: \'react-devtools-backend-manager\'')) {
        return; // 로그 출력 안 함
      }

      // 🔧 [보안 강화] 민감한 정보 필터링
      if (message.includes('app_id:') ||
        message.includes('uid:') ||
        message.includes('username:') ||
        message.includes('paymentId:') ||
        message.includes('txid:') ||
        message.includes('credentials:') ||
        message.includes('receiving_email:') ||
        message.includes('DonationProvider 초기화 완료') ||
        message.includes('useEffect 실행') ||
        message.includes('로그인 사용자 감지') ||
        message.includes('loadHallOfFameData 호출됨') ||
        message.includes('백엔드 API 호출') ||
        message.includes('Functions 호출') ||
        message.includes('상세 데이터:') ||
        message.includes('piUser:') ||
        message.includes('type: object') ||
        message.includes('초기화되지 않음') ||
        message.includes('대기')) {
        return; // 민감한 로그 출력 안 함
      }
    }

    // 객체 메시지 필터링 (패턴 확장)
    if (args.length > 1 && args[1] && typeof args[1] === 'object') {
      const obj = args[1];
      if ((obj.source === 'react-devtools-content-script' && (obj.hello === true || obj.payload)) ||
        (obj.source === 'react-devtools-bridge' && obj.payload) ||
        (obj.source === 'react-devtools-backend-manager' && obj.payload) ||
        (obj.frontend_url && obj.development_url && obj.slug)) {
        return; // 로그 출력 안 함
      }

      // 🔧 [보안 강화] 민감한 객체 정보 필터링
      if (obj.app_id || obj.uid || obj.username || obj.paymentId || obj.txid ||
        obj.credentials || obj.receiving_email || obj.piUser) {
        return; // 민감한 객체 로그 출력 안 함
      }
    }

    // 필터링되지 않은 메시지만 출력
    originalConsoleLog(...args);
  };

  // 🚀 [추가] console.warn도 필터링 (Pi SDK 'Discarding message'가 warn으로 나올 수 있음)
  console.warn = (...args) => {
    if (shouldSuppressNoisyConsoleArgs(args)) return;
    const message = args[0];
    if (typeof message === 'string') {
      if (message.includes('Discarding message') ||
        message.includes('Prod: false | Sandbox: false') ||
        message.includes('Prod: true | Sandbox: true')) {
        return;
      }
    }
    originalConsoleWarn(...args);
  };

  console.log('[Environment] 🔧 로그 필터링 활성화 - Pi SDK 메시지 정리됨');
};

/**
 * 콘솔 로그 필터링 비활성화
 */
export const disableLogFiltering = () => {
  if (!isLogFilteringEnabled) return;

  isLogFilteringEnabled = false;
  console.log = originalConsoleLog;
  console.warn = originalConsoleWarn;

  console.log('[Environment] 🔧 로그 필터링 비활성화');
};

/**
 * 로그 필터링 상태 확인
 */
export const isLogFilteringActive = () => {
  return isLogFilteringEnabled;
};

// 🚀 [신규] 전역 함수로 개발자 도구에서 접근 가능하게 설정
window.enableLogFiltering = enableLogFiltering;
window.disableLogFiltering = disableLogFiltering;
window.isLogFilteringActive = isLogFilteringActive;

export function errorLog(...args) {
  // 에러는 항상 출력 (보안과 관계없이 중요함)
  console.error(...args);
}

export function warnLog(...args) {
  if (hasLogAccess()) {
    console.warn(...args);
  } else {
    // 프로덕션에서도 경고는 출력하되 민감한 정보 제거
    const sanitizedArgs = args.map(arg =>
      typeof arg === 'string' && arg.includes('uid') ? '[MASKED]' : arg
    );
    console.warn(...sanitizedArgs);
  }
}

/**
 * [보안 강화] 민감한 정보 마스킹 함수 - 추가 필드 및 패턴 지원
 */
export const maskSensitiveData = (data) => {
  if (typeof data !== 'object' || data === null) return data;

  const sensitiveKeys = [
    'userId', 'uid', 'token', 'accessToken', 'paymentId', 'txid',
    'devId', 'adminPassword', 'pi_username', // 기존 필드
    'admin_user', 'adminUser', 'system_auth_key', 'log_access_token'   // 추가 보안 필드
  ];
  const masked = { ...data };

  sensitiveKeys.forEach(key => {
    if (masked[key] && typeof masked[key] === 'string') {
      // [개선] 길이에 따라 마스킹 패턴 조정
      const value = masked[key];
      if (value.length <= 8) {
        masked[key] = value.substring(0, 2) + '****';
      } else {
        masked[key] = value.substring(0, 4) + '****' + value.substring(value.length - 2);
      }
    }
  });

  return masked;
};

/**
 * 🚀 [성능 최적화] 디바운싱 유틸리티 함수
 * Pi SDK 메시지 처리 성능 개선용
 */
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

/**
 * 🚀 [성능 최적화] 스로틀링 유틸리티 함수
 * Pi SDK 메시지 처리 성능 개선용
 */
export const throttle = (func, limit) => {
  let inThrottle;
  return function executedFunction(...args) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
};

/**
 * [🔧 수정] 도메인별 환경 매핑 - SpotGo 도메인 추가
 */
const DOMAIN_ENVIRONMENT_MAP = {
  // 'localhost': 'sandbox', // ❌ Removed: Handled by top-priority check
  'dev-spot.painori.com': 'sandbox',
  'testspot.painori.com': 'testnet',
  'admin.painori.com': 'testnet',        // ✅ Admin 전용 도메인 (기본 testnet)
  'spotnori1967.pinet.com': 'testnet',   // Testnet (레거시)
  'spotgo1762.pinet.com': 'testnet',     // ✅ Testnet (SpotGo 신규)
  'hackathon.painori.com': 'testnet',
  'spot.painori.com': 'production',
  'spotnori7417.pinet.com': 'production', // Mainnet (레거시)
  'spotgo5878.pinet.com': 'production'    // ✅ Mainnet (SpotGo 신규)
};

/**
 * [핵심 수정] Pi Browser 환경 정밀 감지 - 실제 환경에 맞게 완전 재설계
 * Pi Browser 실제 동작 방식:
 * - hostname: 'sandbox.minepi.com' 또는 'app.minepi.com'
 * - isInIframe: false (직접 접속)
 * - referrer: 'https://sandbox.minepi.com/mobile-app-ui/app/spot-nori'
 */
export const isPiBrowserEnvironment = () => {
  const hostname = window.location.hostname;
  const referrer = document.referrer;

  // [핵심 수정] Pi Browser 실제 환경 조건
  // 1. minepi.com 도메인에서 직접 실행 (sandbox.minepi.com, app.minepi.com)
  const isPiDomain = hostname.includes('minepi.com');

  // 2. minepi.com에서 온 referrer (Pi 앱 내부에서 접속)
  const hasPiReferrer = referrer.includes('minepi.com');

  // 3. [추가] Pi SDK 존재 여부 확인 (더 정확한 판단)
  const hasPiSDK = !!window.Pi;

  // 4. [추가] Pi Browser User-Agent 패턴 확인
  const userAgent = navigator.userAgent.toLowerCase();
  const hasPiUserAgent = userAgent.includes('pibrowser') ||
    userAgent.includes('pi network') ||
    userAgent.includes('pi-network');

  // Pi Browser 환경 판단: 도메인/리퍼러 조건 + (SDK 또는 User-Agent)
  const isPiBrowser = (isPiDomain || hasPiReferrer) && (hasPiSDK || hasPiUserAgent);

  debugLog('[Environment] Pi Browser 감지:', {
    hostname,
    isPiDomain,
    hasPiReferrer,
    hasPiSDK,
    hasPiUserAgent,
    result: isPiBrowser
  });

  return isPiBrowser;
};

/**
 * [🎯 신규 추가] Pi SDK sandbox 플래그 결정 함수
 * PC 샌드박스 환경에서 실행 중인지 정확히 감지
 * 
 * @returns {boolean} PC 샌드박스 환경이면 true, 그 외는 false
 * 
 * 판단 기준:
 * 1. iframe 내부에서 실행 중인가?
 * 2. referrer가 sandbox.minepi.com인가?
 * 3. ancestorOrigins에 sandbox.minepi.com이 포함되어 있는가?
 */
export const isPiSandboxEnvironment = () => {
  // 1. iframe 내부에서 실행 중인지 확인
  const isInIframe = window.parent !== window;

  // 2. 여러 방법으로 sandbox.minepi.com 감지
  const referrer = document.referrer;
  const referrerCheck = referrer.includes('sandbox.minepi.com');

  // 3. ancestorOrigins 확인 (Chrome, Edge 등에서 지원)
  const ancestorOriginCheck = window.location.ancestorOrigins?.[0]?.includes('sandbox.minepi.com') || false;

  // 4. postMessage origin 확인 (가능한 경우)
  let postMessageCheck = false;
  try {
    // iframe 내부이고 parent 접근이 가능한 경우
    if (isInIframe && window.parent.location.href.includes('sandbox.minepi.com')) {
      postMessageCheck = true;
    }
  } catch (e) {
    // Cross-origin 에러는 무시 (보안상 parent 접근 불가)
  }

  // 5. 종합 판단 - 하나라도 true면 샌드박스 환경
  const isSandbox = referrerCheck || ancestorOriginCheck || postMessageCheck;

  // 6. 디버그 로그 (기존 시스템 활용)
  debugLog('[Environment] Pi Sandbox 감지:', {
    isInIframe,
    referrer: referrer ? referrer.substring(0, 50) + '...' : '',
    referrerCheck,
    ancestorOriginCheck,
    ancestorOrigins: window.location.ancestorOrigins ? [...window.location.ancestorOrigins] : [],
    postMessageCheck,
    final: isSandbox
  });

  return isSandbox;
};

/**
 * [멀티 플랫폼 지원 - 근본적 개선]
 * 현재 실행 중인 플랫폼을 정확하게 감지하는 함수
 * 
 * @returns {string} 'pi' 또는 'web'
 * 
 * 판단 기준 (모든 조건을 종합적으로 검증):
 * 1. Pi 브라우저 도메인에서 실행 중인가?
 * 2. iframe 내부에서 실행 중인가?
 * 3. referrer가 minepi.com인가?
 * 4. Pi SDK와 실제 통신이 가능한가?
 * 5. [추가] URL에 Pi 브라우저 관련 파라미터가 있는가?
 */
export const getPlatform = () => {
  // [최적화] 캐시된 값이 있으면 즉시 반환
  if (ENVIRONMENT_CACHE.platform !== null) {
    return ENVIRONMENT_CACHE.platform;
  }

  const hostname = window.location.hostname;
  const isInIframe = window.parent !== window;
  const referrer = document.referrer;
  const hasPiObject = !!window.Pi;

  // [추가] URL 파라미터 확인 - Pi 브라우저는 특정 파라미터를 추가할 수 있음
  const urlParams = new URLSearchParams(window.location.search);
  const hasPiParams = urlParams.has('pi-browser') || urlParams.has('pi_browser');

  // [추가] 현재 URL 확인 - Pi 브라우저는 특정 URL 패턴을 사용
  const currentUrl = window.location.href;
  const isPiUrlPattern = currentUrl.includes('.minepi.com/app/') ||
    currentUrl.includes('/app/spot-nori');

  // Pi 브라우저 공식 도메인들
  const piDomains = [
    'sandbox.minepi.com',
    'app.minepi.com',
    'app-cdn.minepi.com'
  ];

  // 도메인 검증
  const isOnPiDomain = piDomains.some(domain =>
    hostname === domain || hostname.endsWith('.' + domain)
  );

  // Referrer 검증
  const hasPiReferrer = referrer.includes('minepi.com');

  // Pi 브라우저 User-Agent 패턴 (확장)
  const userAgent = navigator.userAgent.toLowerCase();
  const hasPiUserAgent = userAgent.includes('pibrowser') ||
    userAgent.includes('pi network') ||
    userAgent.includes('pi-network') ||
    userAgent.includes('pinetwork') ||
    // [추가] 모바일 Pi 브라우저 패턴
    (userAgent.includes('mobile') && userAgent.includes('pi'));

  // [추가] Pi Browser 직접 접속 감지 - window.Pi 객체와 User-Agent 조합
  const isPiBrowserDirect = hasPiObject && (hasPiUserAgent || isPiUrlPattern);

  // [추가] Pi SDK 메서드 존재 여부 확인
  const hasPiMethods = hasPiObject &&
    typeof window.Pi.authenticate === 'function' &&
    typeof window.Pi.createPayment === 'function';

  // 종합 판단 (조건 완화)
  const isPiBrowser = isOnPiDomain ||
    (isInIframe && hasPiReferrer) ||
    isPiBrowserDirect ||
    hasPiParams ||
    isPiUrlPattern ||
    hasPiMethods;

  // [최적화] 디버깅 정보 - 개발 환경에서만 출력
  debugLog('[Environment] Platform detection details:', {
    hostname,
    isOnPiDomain,
    isInIframe,
    referrer: referrer ? referrer.substring(0, 50) + '...' : '', // 민감한 정보 축약
    hasPiReferrer,
    hasPiObject,
    hasPiUserAgent,
    hasPiParams,
    isPiUrlPattern,
    hasPiMethods,
    isPiBrowserDirect,
    userAgent: userAgent.substring(0, 100) + '...', // 로그 간소화
    result: isPiBrowser ? 'pi' : 'web'
  });

  // [추가] Pi 브라우저로 감지되었지만 Pi 객체가 없는 경우 경고
  if (isPiBrowser && !hasPiObject) {
    warnLog('[Environment] Pi Browser detected but Pi SDK not loaded yet');
  }

  // [최적화] 결과 캐싱
  const result = isPiBrowser ? 'pi' : 'web';
  ENVIRONMENT_CACHE.platform = result;

  // Pi SDK가 있어도 Pi 브라우저 환경이 아니면 web으로 처리
  return result;
};

/**
 * 🔥🔥🔥 [2025-08-19 최종 수정] Pi Network 환경(sandbox/testnet/production)을 확인하는 함수
 * 
 * @returns {string} 'sandbox', 'testnet', 또는 'production'
 * 
 * 우선순위 (추측 제거, 명확한 근거만 사용):
 * 1. Pi SDK environment (최우선)
 * 2. URL 파라미터 env (개발/디버깅용)
 * 3. 도메인 기반 판단 (명시적 매핑)
 * 4. 최종 폴백: sandbox (가장 안전한 기본값)
 * 
 * ❌ 제거됨: "Pi Browser면 production" 추측 로직
 */
export const getEnvironment = () => {
  // [최적화] 캐시된 값이 있으면 즉시 반환
  if (ENVIRONMENT_CACHE.environment !== null) {
    return ENVIRONMENT_CACHE.environment;
  }

  // 🚀 [최우선] 로컬 개발 환경 - 무조건 Testnet DB 사용 (사용자 요청)
  const localHostname = window.location.hostname;
  if (localHostname === 'localhost' || localHostname.startsWith('127.') || localHostname.startsWith('192.168.')) {
    debugLog('[Environment] 🚀 Local Development: Forcing use of TESTNET DB');
    ENVIRONMENT_CACHE.environment = 'testnet';
    return 'testnet';
  }

  // 🎯 1순위: Pi SDK environment (최우선 - 가장 신뢰할 수 있는 소스)
  if (window.Pi?.environment) {
    const piEnv = window.Pi.environment;
    // Pi SDK는 'sandbox' 또는 'production'만 반환
    // testnet은 sandbox로 취급
    const result = piEnv === 'production' ? 'production' : 'sandbox';

    debugLog('[Environment] 🎯 1순위: Pi SDK 정보로 결정:', result);
    ENVIRONMENT_CACHE.environment = result;
    return result;
  }

  // ❌❌❌ [핵심 수정] "Pi Browser면 production" 추측 로직 완전 제거
  // 이전 코드:
  // if (isPiBrowserEnvironment()) {
  //   const isSandbox = isPiSandboxEnvironment();
  //   const result = isSandbox ? 'sandbox' : 'production';
  //   ...
  // }
  // 이 로직이 테스트넷을 메인넷으로 오인식하게 만드는 원인이었음

  // 🎯 2순위: URL 파라미터로 강제 지정 (개발자용)
  const urlParams = new URLSearchParams(window.location.search);
  const envParam = urlParams.get('env');
  if (envParam && ['sandbox', 'testnet', 'production'].includes(envParam)) {
    debugLog('[Environment] 🎯 2순위: URL 파라미터로 결정:', envParam);
    ENVIRONMENT_CACHE.environment = envParam;
    return envParam;
  }

  // 🎯 3순위: 도메인 주소로 명확하게 판단
  const hostname = window.location.hostname;

  // Pi Network 도메인 감지 로직 (보수적 접근)
  if (hostname === 'sandbox.minepi.com' || hostname.endsWith('.sandbox.minepi.com')) {
    debugLog('[Environment] 🎯 3순위: sandbox.minepi.com 도메인 감지 - sandbox');
    debugLog('[Environment] 🔍 Pi Network 샌드박스 환경 확인:', { hostname, referrer: document.referrer });
    ENVIRONMENT_CACHE.environment = 'sandbox';
    return 'sandbox';
  }

  // 메인넷 도메인은 정확히 매칭 (보수적 접근)
  if (hostname === 'app.minepi.com' || hostname === 'minepi.com') {
    debugLog('[Environment] 🎯 3순위: minepi.com 메인 도메인 감지 - production');
    debugLog('[Environment] 🔍 Pi Network 프로덕션 환경 확인:', { hostname, referrer: document.referrer });
    ENVIRONMENT_CACHE.environment = 'production';
    return 'production';
  }

  const mappedEnv = DOMAIN_ENVIRONMENT_MAP[hostname];
  if (mappedEnv) {
    debugLog('[Environment] 🎯 3순위: 도메인 매핑으로 결정:', mappedEnv);
    ENVIRONMENT_CACHE.environment = mappedEnv;
    return mappedEnv;
  }

  // 추가 도메인 패턴 확인 (painori.com 하위 도메인)
  if (hostname.includes('painori.com')) {
    // spot.painori.com만 production, testspot은 testnet, 나머지는 sandbox
    if (hostname === 'spot.painori.com') {
      debugLog('[Environment] Mainnet domain detected: production');
      ENVIRONMENT_CACHE.environment = 'production';
      return 'production';
    } else if (hostname === 'testspot.painori.com') {
      debugLog('[Environment] Testnet domain detected: testnet');
      ENVIRONMENT_CACHE.environment = 'testnet';
      return 'testnet';
    } else {
      debugLog('[Environment] Painori.com subdomain detected: sandbox');
      ENVIRONMENT_CACHE.environment = 'sandbox';
      return 'sandbox';
    }
  }

  // (Prev Location of localhost check - Removed)

  // URL에서 메인넷 App ID 감지 (Pi Browser 지원)
  const currentUrl = window.location.href;
  if (currentUrl.includes('spot-nori-58b6af4480fae19f')) {
    debugLog('[Environment] 메인넷 App ID 감지 - production 환경');
    ENVIRONMENT_CACHE.environment = 'production';
    return 'production';
  }

  // minepi.com 도메인 체크 (명시적)
  if (currentUrl.includes('sandbox.minepi.com')) {
    debugLog('[Environment] Sandbox URL detected - using sandbox environment');
    ENVIRONMENT_CACHE.environment = 'sandbox';
    return 'sandbox';
  }

  if (currentUrl.includes('app.minepi.com')) {
    // app.minepi.com은 production일 가능성이 높지만, Pi SDK 없이는 확실하지 않음
    // 안전을 위해 sandbox로 처리
    debugLog('[Environment] app.minepi.com detected but no Pi SDK - using sandbox for safety');
    ENVIRONMENT_CACHE.environment = 'sandbox';
    return 'sandbox';
  }

  // 환경 변수 확인 (최후의 수단) - 안전한 접근
  const envFromConfig = typeof process !== 'undefined' && process.env ? process.env.REACT_APP_PI_ENV : null;
  if (envFromConfig && ['sandbox', 'testnet', 'production'].includes(envFromConfig)) {
    debugLog('[Environment] Using value from environment variable (fallback):', envFromConfig);
    ENVIRONMENT_CACHE.environment = envFromConfig;
    return envFromConfig;
  }

  // 🎯 최종 폴백: 명확한 근거 없음, 안전한 기본값(sandbox) 사용
  const finalDefault = 'sandbox';
  debugLog('[Environment] 🎯 최종 폴백: 명확한 근거 없음, 안전한 기본값(sandbox) 사용');
  ENVIRONMENT_CACHE.environment = finalDefault;
  return finalDefault;
};

/**
 * [신규 추가]
 * Firebase 환경을 결정하는 함수
 * 
 * @returns {string} 'development' 또는 'production'
 * 
 * 판단 기준:
 * - spot.painori.com → production (메인넷)
 * - 그 외 모든 환경 → development (로컬/테스트넷)
 */
export const getFirebaseEnvironment = () => {
  // [최적화] 캐시된 값이 있으면 즉시 반환
  if (ENVIRONMENT_CACHE.firebaseEnvironment !== null) {
    return ENVIRONMENT_CACHE.firebaseEnvironment;
  }

  const hostname = window.location.hostname;

  // 메인넷은 production Firebase 사용
  if (hostname === 'spot.painori.com') {
    debugLog('[Environment] Firebase production environment (mainnet)');
    ENVIRONMENT_CACHE.firebaseEnvironment = 'production';
    return 'production';
  }

  // 로컬, dev-spot.painori.com, testspot.painori.com 등은 development Firebase 사용
  debugLog('[Environment] Firebase development environment (local/testnet)');
  ENVIRONMENT_CACHE.firebaseEnvironment = 'development';
  return 'development';
};

/**
 * [신규 추가]
 * UI에 표시할 환경명을 반환하는 함수
 * 사용자가 이해하는 용어(testnet/mainnet)로 변환
 * 
 * @returns {string} 'testnet' 또는 'mainnet'
 */
export const getDisplayEnvironment = () => {
  // [최적화] 캐시된 값이 있으면 즉시 반환
  if (ENVIRONMENT_CACHE.displayEnvironment !== null) {
    return ENVIRONMENT_CACHE.displayEnvironment;
  }

  const piEnv = getEnvironment();
  const result = (piEnv === 'sandbox' || piEnv === 'testnet') ? 'testnet' : 'mainnet';
  ENVIRONMENT_CACHE.displayEnvironment = result;
  return result;
};

/**
 * [신규 추가]
 * 현재 환경이 개발 환경인지 확인하는 헬퍼 함수
 * 
 * @returns {boolean} 개발 환경 여부
 */
export const isDevelopment = () => {
  // [최적화] 캐시된 값이 있으면 즉시 반환
  if (ENVIRONMENT_CACHE.isDevelopment !== null) {
    return ENVIRONMENT_CACHE.isDevelopment;
  }

  const result = (typeof process !== 'undefined' && process.env ? process.env.NODE_ENV === 'development' : false) ||
    window.location.hostname === 'localhost';
  ENVIRONMENT_CACHE.isDevelopment = result;
  return result;
};

/**
 * [신규 추가]
 * 현재 사용자가 게스트인지 확인하는 헬퍼 함수
 * 향후 게스트 로그인 구현 시 사용
 * 
 * @returns {boolean} 게스트 여부
 */
export const isGuestUser = () => {
  const userType = sessionStorage.getItem('user-type');
  return userType === 'guest';
};

/**
 * [🔧 핵심 수정] Pi SDK 로드가 필요한지 확인하는 함수 - PiNet 도메인 추가
 * 
 * @returns {boolean} SDK 로드 필요 여부
 * 
 * 🔥 수정된 원칙:
 * - minepi.com 도메인이면 무조건 SDK 로드 (기존 유지)
 * - painori.com 도메인들도 SDK 로드 (dev-spot, testspot, spot)
 * - PiNet 도메인들도 SDK 로드 추가 ✅
 * - Pi Network 정책 준수: Pi Browser에서는 SDK 필수
 */
export const shouldLoadPiSDK = () => {
  // [최적화] 캐시된 값이 있으면 즉시 반환
  if (ENVIRONMENT_CACHE.shouldLoadPiSDK !== null) {
    return ENVIRONMENT_CACHE.shouldLoadPiSDK;
  }

  const hostname = window.location.hostname;

  // [🎯 핵심 수정] minepi.com 도메인에서는 무조건 SDK 로드 (기존 유지)
  if (hostname.includes('minepi.com')) {
    debugLog('[Environment] Pi Browser 환경 - SDK 로드 필수');
    ENVIRONMENT_CACHE.shouldLoadPiSDK = true;
    return true;
  }

  // [🔧 핵심 추가] painori.com 도메인들과 PiNet 도메인들도 SDK 로드 대상에 추가
  if (hostname === 'dev-spot.painori.com' ||
    hostname === 'testspot.painori.com' ||
    hostname === 'admin.painori.com' ||       // ✅ Admin 전용 도메인
    hostname === 'spot.painori.com' ||
    hostname === 'spotnori1967.pinet.com' ||  // Testnet PiNet (레거시)
    hostname === 'spotgo1762.pinet.com' ||    // ✅ Testnet PiNet (SpotGo)
    hostname === 'spotnori7417.pinet.com' ||  // Mainnet PiNet (레거시)
    hostname === 'spotgo5878.pinet.com') {    // ✅ Mainnet PiNet (SpotGo)
    debugLog('[Environment] Painori.com/PiNet 도메인 - Pi 개발자센터 연동을 위한 SDK 로드 필요');
    ENVIRONMENT_CACHE.shouldLoadPiSDK = true;
    return true;
  }

  // [성능 최적화] 일반 웹 환경에서는 SDK 로드 안함
  debugLog('[Environment] 일반 웹 환경 - SDK 로드 불필요');
  ENVIRONMENT_CACHE.shouldLoadPiSDK = false;
  return false;
};

/**
 * Pi SDK 준비 상태 정밀 체크
 * 인증 실패 문제 해결용
 */
export const isPiSDKReady = () => {
  if (!window.Pi) return false;

  // 기본 메서드 존재 확인
  const hasBasicMethods = typeof window.Pi.authenticate === 'function' &&
    typeof window.Pi.createPayment === 'function';

  // 초기화 상태 확인 (Pi SDK가 설정하는 플래그들)
  const isInitialized = window.Pi.initialized !== false &&
    window.Pi.initializing !== true;

  // 환경 설정 완료 확인
  const hasEnvironment = !!window.Pi.environment;

  const isReady = hasBasicMethods && isInitialized && hasEnvironment;

  debugLog('[Environment] Pi SDK readiness check:', {
    hasBasicMethods,
    isInitialized,
    hasEnvironment,
    isReady
  });

  return isReady;
};

/**
 * [신규 추가]
 * 전체 환경 정보를 한 번에 가져오는 함수
 * 디버깅 및 로깅에 유용
 * 
 * @returns {Object} 환경 정보 객체
 */
export const getEnvironmentInfo = () => {
  // [최적화] 캐시된 값이 있으면 즉시 반환
  if (ENVIRONMENT_CACHE.environmentInfo !== null) {
    return ENVIRONMENT_CACHE.environmentInfo;
  }

  const info = {
    platform: getPlatform(),
    piEnv: getEnvironment(),
    firebaseEnv: getFirebaseEnvironment(),
    displayEnv: getDisplayEnvironment(),
    isDev: isDevelopment(),
    isGuest: isGuestUser(),
    shouldLoadPiSDK: shouldLoadPiSDK(),
    hostname: window.location.hostname,
    isInIframe: window.parent !== window,
    referrer: document.referrer ? document.referrer.substring(0, 50) + '...' : '', // 민감한 정보 축약
    userAgent: navigator.userAgent.substring(0, 100) + '...' // 축약
  };

  // [최적화] 결과 캐싱 - 게스트 여부는 캐싱하지 않음
  ENVIRONMENT_CACHE.environmentInfo = { ...info, isGuest: isGuestUser() };
  return info;
};

/**
 * [신규 추가]
 * Firestore 컬렉션 경로를 환경에 따라 동적으로 반환
 * 
 * @param {string} baseName - 기본 컬렉션 이름 (예: 'users')
 * @param {string} env - 현재 환경 이름
 * @returns {string} - 접두사가 포함된 컬렉션 이름
 */
export const getCollectionPath = (baseName, env) => {
  // 1. Production/Mainnet은 접두사 없이 사용
  if (!env || env === 'production' || env === 'mainnet') return baseName;

  // 2. Testnet, Sandbox, Development는 'testnet_' 접두사 사용 (백엔드와 통일)
  if (env === 'testnet' || env === 'sandbox' || env === 'development' || env === 'dev') return `testnet_${baseName}`;

  // 3. 그 외는 'dev_' 접두사 사용
  return `dev_${baseName}`;
};

/**
 * [신규 추가]
 * 환경을 재확인하는 함수
 * Pi SDK가 나중에 로드된 경우 환경을 다시 확인할 수 있음
 * 캐시를 무효화하고 새로 판단
 * 
 * @returns {string} 'sandbox', 'testnet', 또는 'production'
 */
export const refreshEnvironment = () => {
  debugLog('[Environment] ===== Environment refresh 시작 =====');

  // [중요] 모든 관련 캐시 초기화
  ENVIRONMENT_CACHE.environment = null;
  ENVIRONMENT_CACHE.displayEnvironment = null;
  ENVIRONMENT_CACHE.firebaseEnvironment = null;
  ENVIRONMENT_CACHE.environmentInfo = null;

  // 환경 재판단 - getEnvironment()의 로직과 동일하게 적용
  const env = getEnvironment();

  debugLog('[Environment] Environment refresh 결과:', env);
  debugLog('[Environment] ===== Environment refresh 완료 =====');

  return env;
};

/**
 * 테스트 모드 접근 권한 확인 함수 - URL 파라미터 우선 방식
 * [2025-12-19 수정] Remote Config 의존성 제거로 캐시 문제 해결
 */
const hasTestAccess = () => {
  try {
    // URL 파라미터 확인
    const urlParams = new URLSearchParams(window.location.search);
    const testParam = urlParams.get('test');

    // test 파라미터가 없으면 즉시 false 반환 (성능 최적화)
    if (!testParam) {
      return false;
    }

    // 🚀 [수정] URL 파라미터만으로 판단 (Remote Config 의존성 제거)
    // 개발자만 사용하므로 보안상 문제없음
    return testParam === 'lukepai2025';

  } catch (error) {
    // 예외 발생 시 안전하게 false 반환
    console.error('[Environment] 테스트 모드 액세스 검증 중 오류:', error);
    return false;
  }
};

/**
 * [최적화] 캐시 초기화 함수 - 테스트 또는 특수한 경우에만 사용
 * 모든 캐시된 환경 정보를 초기화
 */
export const clearEnvironmentCache = () => {
  // 환경 관련 캐시 초기화
  Object.keys(ENVIRONMENT_CACHE).forEach(key => {
    ENVIRONMENT_CACHE[key] = null;
  });

  // 로그 캐시도 초기화 (현재 사용하지 않음)
  // LOG_ACCESS_CACHE = null;
  // LOG_ACCESS_CHECKED = false;

  // 테스트 모드 캐시도 초기화
  window.__testAccessCache = undefined;
  window.__testAccessChecked = false;

  debugLog('[Environment] 모든 캐시가 초기화되었습니다');
};

// hasLogAccess, hasTestAccess 함수 export 추가
export { hasLogAccess, hasTestAccess };